import React, { useState, useEffect } from 'react';
import BurialSizeSelector, { BurialSize } from '../ui/BurialSizeSelector';
import { CheckIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';

interface Cemetery {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  totalPlots?: number;
  availablePlots?: number;
  providerId?: string;
  providerName?: string;
  assetName?: string;
  isReal?: boolean; // To distinguish between real and mock data
}

interface BurialPlotBookingData {
  burialSize: BurialSize;
  preferredCemetery: string; // Changed from array to single string
  preparationDate?: string;
  urgencyLevel: 'URGENT' | 'STANDARD';
  specialRequests?: string;
}

interface PricingResult {
  basePrice: number;
  urgencySurcharge: number;
  total: number;
  breakdown: string;
  currency?: string;
}

interface BurialPlotBookingFormProps {
  caseId?: string;
  initialData?: Partial<BurialPlotBookingData>;
  onSubmit: (data: BurialPlotBookingData, pricing: PricingResult) => void;
  onCancel: () => void;
  disabled?: boolean;
}

const BurialPlotBookingForm: React.FC<BurialPlotBookingFormProps> = ({
  caseId,
  initialData,
  onSubmit,
  onCancel,
  disabled = false,
}) => {
  const [availableCemeteries, setAvailableCemeteries] = useState<Cemetery[]>([]);
  const [cemeteriesLoading, setCemeteriesLoading] = useState(true);

  // Fetch cemeteries from database with optional plot size filter
  const fetchCemeteries = async (plotSize?: BurialSize) => {
    try {
      setCemeteriesLoading(true);

      // Build API URL with plot size filter if provided
      const apiUrl = plotSize
        ? `/cemeteries/with-burial-plots?plotSize=${plotSize}`
        : '/cemeteries/with-burial-plots';

      // Try to fetch real cemeteries from the database
      // This endpoint should aggregate burial plot assets and provider information
      const response = await api.get(apiUrl);
      const realCemeteries: Cemetery[] = response.data.map((cemetery: any) => ({
        ...cemetery,
        isReal: true
      }));

      // For plot size filtered results, only show real data
      // For unfiltered results, combine real and mock data
      let finalCemeteries: Cemetery[];

      if (plotSize) {
        // Only use real data for filtered results - ensures accuracy
        finalCemeteries = realCemeteries;

        // Show warning if no cemeteries available for selected plot size
        if (realCemeteries.length === 0) {
          console.log(`No cemeteries available for ${plotSize} plots`);
        }
      } else {
        // Mock cemetery data as fallback and additional options for unfiltered results
        const mockCemeteries: Cemetery[] = [
          {
            id: 'kl-general-1',
            name: 'Kuala Lumpur General Cemetery',
            address: 'Jalan Kuarters Hulu Kelang, Setapak',
            city: 'Kuala Lumpur',
            state: 'Kuala Lumpur',
            totalPlots: 150,
            availablePlots: 42,
            isReal: false
          },
          {
            id: 'subang-memorial-1',
            name: 'Subang Jaya Memorial Park',
            address: 'Persiaran Kewajipan, Subang Jaya',
            city: 'Subang Jaya',
            state: 'Selangor',
            totalPlots: 200,
            availablePlots: 68,
            isReal: false
          },
          {
            id: 'ns-islamic-1',
            name: 'Negeri Sembilan Islamic Cemetery',
            address: 'Jalan Tunku Kurshiah, Seremban',
            city: 'Seremban',
            state: 'Negeri Sembilan',
            totalPlots: 100,
            availablePlots: 25,
            isReal: false
          }
        ];

        // Combine real and mock data, with real data taking priority
        // Remove duplicates by name
        finalCemeteries = [...realCemeteries];
        mockCemeteries.forEach(mock => {
          if (!finalCemeteries.some(real => real.name.toLowerCase() === mock.name.toLowerCase())) {
            finalCemeteries.push(mock);
          }
        });
      }

      setAvailableCemeteries(finalCemeteries);
    } catch (error) {
      console.error('Failed to fetch cemeteries:', error);

      // Use only mock data as fallback for unfiltered results
      if (!plotSize) {
        const mockCemeteries: Cemetery[] = [
          {
            id: 'kl-general-1',
            name: 'Kuala Lumpur General Cemetery',
            address: 'Jalan Kuarters Hulu Kelang, Setapak',
            city: 'Kuala Lumpur',
            state: 'Kuala Lumpur',
            totalPlots: 150,
            availablePlots: 42,
            isReal: false
          },
          {
            id: 'subang-memorial-1',
            name: 'Subang Jaya Memorial Park',
            address: 'Persiaran Kewajipan, Subang Jaya',
            city: 'Subang Jaya',
            state: 'Selangor',
            totalPlots: 200,
            availablePlots: 68,
            isReal: false
          },
          {
            id: 'ns-islamic-1',
            name: 'Negeri Sembilan Islamic Cemetery',
            address: 'Jalan Tunku Kurshiah, Seremban',
            city: 'Seremban',
            state: 'Negeri Sembilan',
            totalPlots: 100,
            availablePlots: 25,
            isReal: false
          }
        ];

        setAvailableCemeteries(mockCemeteries);
      } else {
        setAvailableCemeteries([]);
      }
    } finally {
      setCemeteriesLoading(false);
    }
  };

  // Fetch cemeteries on component mount
  useEffect(() => {
    fetchCemeteries();
  }, []);

  const [formData, setFormData] = useState<BurialPlotBookingData>({
    burialSize: initialData?.burialSize || 'ADULT',
    preferredCemetery: initialData?.preferredCemetery || '', // Changed from array to single string
    preparationDate: initialData?.preparationDate || '',
    urgencyLevel: initialData?.urgencyLevel || 'STANDARD',
    specialRequests: initialData?.specialRequests || '',
  });

  // Refetch cemeteries when burial size changes
  useEffect(() => {
    if (formData.burialSize) {
      fetchCemeteries(formData.burialSize);
      // Clear selected cemetery when plot size changes to avoid invalid selections
      setFormData(prev => ({
        ...prev,
        preferredCemetery: '' // Changed from array to single string
      }));
    }
  }, [formData.burialSize]);

  const [pricing, setPricing] = useState<PricingResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchQuery, setSearchQuery] = useState('');

  // Calculate pricing when burial size or urgency changes
  useEffect(() => {
    calculatePricing();
  }, [formData.burialSize, formData.urgencyLevel]);

  const calculatePricing = async () => {
    setIsCalculating(true);
    try {
      // REDIPAY INTEGRATION: Flat rate pricing for RediPay RM2.00 maximum
      await new Promise(resolve => setTimeout(resolve, 500));

      // Original pricing (commented for future use):
      // const basePrice = formData.burialSize === 'ADULT' ? 2500 : 1500;
      // const urgencySurcharge = formData.urgencyLevel === 'URGENT' ? 500 : 0;

      const basePrice = 2.00; // RM2.00 flat rate for all burial plot services
      const urgencySurcharge = 0; // No urgency surcharge for RediPay integration
      const total = basePrice + urgencySurcharge;

      setPricing({
        basePrice,
        urgencySurcharge,
        total,
        breakdown: `${formData.burialSize.toLowerCase()} burial plot - RediPay flat rate${urgencySurcharge > 0 ? ` + Urgent processing: RM ${urgencySurcharge}` : ''}`,
        currency: 'MYR',
      });
    } catch (error) {
      console.error('Failed to calculate pricing:', error);
    } finally {
      setIsCalculating(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.preferredCemetery || formData.preferredCemetery.trim() === '') {
      newErrors.preferredCemetery = 'Please select a preferred cemetery location';
    }

    if (!formData.urgencyLevel) {
      newErrors.urgencyLevel = 'Please select urgency level';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !pricing) {
      return;
    }

    onSubmit(formData, pricing);
  };

  const handleInputChange = (
    field: keyof BurialPlotBookingData,
    value: any
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleCemeterySelect = (cemeteryId: string) => {
    setFormData(prev => ({
      ...prev,
      preferredCemetery: cemeteryId // Set to single selected cemetery
    }));

    // Clear error when user selects a cemetery
    if (errors.preferredCemetery) {
      setErrors(prev => ({
        ...prev,
        preferredCemetery: '',
      }));
    }
  };

  const urgencyOptions = [
    {
      value: 'STANDARD',
      label: 'Standard',
      description: '24-48 hours preparation time',
    },
    {
      value: 'URGENT',
      label: 'Urgent',
      description: 'Within 12 hours (additional RM 500)',
    },
  ];

  // Filter cemeteries based on search query
  const filteredCemeteries = availableCemeteries.filter(cemetery => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase().trim();
    return (
      cemetery.name.toLowerCase().includes(query) ||
      cemetery.city.toLowerCase().includes(query) ||
      cemetery.state.toLowerCase().includes(query) ||
      cemetery.address.toLowerCase().includes(query)
    );
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Burial Details Section */}
      <div className="bg-white rounded-lg p-6 border border-neutral-200">
        <h2 className="text-lg font-medium text-neutral-900 mb-4">
          Burial Details
        </h2>

        <div className="space-y-6">
          {/* Burial Size Selector */}
          <BurialSizeSelector
            value={formData.burialSize}
            onChange={(value) => handleInputChange('burialSize', value)}
            disabled={disabled}
            error={errors.burialSize}
          />

          {/* Preferred Cemetery Location */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Preferred Cemetery Location
            </label>
            <p className="text-xs text-neutral-500 mb-3">
              Select one preferred cemetery location. We'll match you with available burial plots in this area.
            </p>

            {/* Search Input */}
            <div className="relative mb-3">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, city, or state..."
                disabled={disabled}
                className="w-full px-4 py-2 pl-10 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-neutral-400 hover:text-neutral-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Search Results Count */}
            {searchQuery && (
              <div className="mb-2">
                <span className="text-xs text-neutral-500">
                  Found {filteredCemeteries.length} location{filteredCemeteries.length !== 1 ? 's' : ''} matching "{searchQuery}"
                </span>
              </div>
            )}

            {/* Cemetery List */}
            <div className="space-y-2 max-h-64 overflow-y-auto border border-neutral-200 rounded-lg p-3">
              {cemeteriesLoading ? (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-8 h-8 mb-3">
                    <svg className="animate-spin h-5 w-5 text-primary-600" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  <p className="text-sm text-neutral-500">Loading cemetery locations...</p>
                </div>
              ) : filteredCemeteries.length > 0 ? (
                filteredCemeteries.map((cemetery) => {
                  const isSelected = formData.preferredCemetery === cemetery.id;

                  return (
                    <div
                      key={cemetery.id}
                      onClick={() => !disabled && handleCemeterySelect(cemetery.id)}
                      className={`
                        relative p-3 rounded-lg border cursor-pointer transition-all
                        ${isSelected
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-neutral-200 bg-white hover:border-neutral-300'
                        }
                        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                      `}
                    >
                      <div className="flex items-start">
                        <div className="flex-shrink-0 mt-1">
                          <div className={`
                            w-4 h-4 rounded-full border-2 flex items-center justify-center
                            ${isSelected
                              ? 'border-primary-500 bg-primary-500'
                              : 'border-neutral-300'
                            }
                          `}>
                            {isSelected && (
                              <div className="w-2 h-2 rounded-full bg-white" />
                            )}
                          </div>
                        </div>

                        <div className="ml-3 flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-medium text-neutral-900 flex items-center">
                              {cemetery.name}
                              {cemetery.isReal && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  Available
                                </span>
                              )}
                            </h4>
                            <div className="flex items-center space-x-2">
                              <span className={`
                                inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                                ${cemetery.availablePlots && cemetery.availablePlots > 20
                                  ? 'bg-green-100 text-green-800'
                                  : cemetery.availablePlots && cemetery.availablePlots > 5
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                                }
                              `}>
                                {cemetery.availablePlots || 0} available
                              </span>
                            </div>
                          </div>

                          <p className="text-xs text-neutral-600 mt-1">
                            {cemetery.address}
                          </p>

                          <div className="flex items-center space-x-3 mt-1">
                            <span className="text-xs text-neutral-500">
                              {cemetery.city}, {cemetery.state}
                            </span>
                            {cemetery.totalPlots && (
                              <span className="text-xs text-neutral-500">
                                {cemetery.availablePlots} / {cemetery.totalPlots} plots
                              </span>
                            )}
                            {cemetery.providerName && (
                              <span className="text-xs text-neutral-500">
                                Provided by {cemetery.providerName}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <div className="text-neutral-400 mb-2">
                    <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {searchQuery ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      )}
                    </svg>
                  </div>
                  <p className="text-sm text-neutral-500 mb-2">
                    {searchQuery ? (
                      <>No cemeteries found matching "{searchQuery}"</>
                    ) : formData.burialSize && availableCemeteries.length === 0 ? (
                      <>No cemeteries available for {formData.burialSize.toLowerCase()} burial plots at the moment</>
                    ) : (
                      <>No cemeteries found</>
                    )}
                  </p>
                  {searchQuery ? (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="mt-2 text-xs text-primary-600 hover:text-primary-800 font-medium"
                    >
                      Clear search to see all locations
                    </button>
                  ) : formData.burialSize && availableCemeteries.length === 0 ? (
                    <div className="space-y-2">
                      <p className="text-xs text-neutral-400">
                        Try selecting Adult plot size or check back later for more availability
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, burialSize: 'ADULT' }));
                        }}
                        className="mt-2 text-xs text-primary-600 hover:text-primary-800 font-medium"
                      >
                        Try Adult plot size instead
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {errors.preferredCemetery && (
              <p className="text-sm text-red-600 mt-1">{errors.preferredCemetery}</p>
            )}

            {formData.preferredCemetery && (
              <div className="mt-2">
                <span className="text-xs text-neutral-600">
                  1 location selected
                </span>
              </div>
            )}
          </div>

          {/* Preparation Date */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Preparation Date (Optional)
            </label>
            <input
              type="datetime-local"
              value={formData.preparationDate}
              onChange={(e) => handleInputChange('preparationDate', e.target.value)}
              disabled={disabled}
              className="input"
              min={new Date().toISOString().slice(0, 16)}
            />
            <p className="text-xs text-neutral-500 mt-1">
              Leave empty for ASAP scheduling
            </p>
          </div>

          {/* Urgency Level */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-4">
              Urgency Level
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {urgencyOptions.map((option) => (
                <div
                  key={option.value}
                  className={`
                    relative rounded-lg border-2 p-4 cursor-pointer transition-all
                    ${formData.urgencyLevel === option.value
                      ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                      : 'border-neutral-200 bg-white hover:border-neutral-300'
                    }
                    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                  onClick={() => !disabled && handleInputChange('urgencyLevel', option.value)}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h3 className="font-medium text-neutral-900">
                          {option.label}
                        </h3>
                        {formData.urgencyLevel === option.value && (
                          <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center">
                            <svg
                              className="w-3 h-3 text-white"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-neutral-600 mt-1">
                        {option.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {errors.urgencyLevel && (
              <p className="text-sm text-red-600 mt-1">{errors.urgencyLevel}</p>
            )}
          </div>

          {/* Special Requests */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Special Requests (Optional)
            </label>
            <textarea
              value={formData.specialRequests}
              onChange={(e) => handleInputChange('specialRequests', e.target.value)}
              disabled={disabled}
              rows={3}
              className="input resize-none"
              placeholder="Any special requirements or preferences..."
            />
          </div>
        </div>
      </div>

      {/* Pricing Summary */}
      {pricing && (
        <div className="bg-neutral-50 rounded-lg p-6 border border-neutral-200">
          <h3 className="text-lg font-medium text-neutral-900 mb-4">
            Pricing Summary
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600">Base Price ({formData.burialSize.toLowerCase()} plot)</span>
              <span className="font-medium">RM {pricing.basePrice}</span>
            </div>
            {pricing.urgencySurcharge > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-neutral-600">Urgency Surcharge</span>
                <span className="font-medium">RM {pricing.urgencySurcharge}</span>
              </div>
            )}
            <div className="border-t border-neutral-200 pt-2">
              <div className="flex justify-between">
                <span className="font-medium text-neutral-900">Total</span>
                <span className="text-lg font-bold text-primary-600">RM {pricing.total}</span>
              </div>
            </div>
            <p className="text-xs text-neutral-500 mt-2">
              {pricing.breakdown}
            </p>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end space-x-4 pt-6">
        <button
          type="button"
          onClick={onCancel}
          disabled={disabled}
          className="btn-outline"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={disabled || isCalculating}
          className="btn-primary"
        >
          {isCalculating ? 'Calculating...' : 'Select Plot'}
        </button>
      </div>
    </form>
  );
};

export default BurialPlotBookingForm;